import {Box, Group, Paper, Stack, Table, Text} from '@mantine/core'
import {useMediaQuery} from '@mantine/hooks'
import {useCallback, useLayoutEffect, useRef, useState} from 'react'
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    Rectangle,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

import type {CapacityBucket, ProspectsByGivingLevelData} from 'src/api/client/models/analytics'
import type {ProspectClickFilters} from 'src/components/analytics/use-prospect-navigation'
import type {ExportKind} from 'src/components/export-image-button'
import {SectionCard} from 'src/components/section-card'
import {CARTESIAN_GRID_PROPS, CHART_COLORS} from 'src/lib/chart-colors'
import {formatCurrency, formatNumber} from 'src/lib/format'

const CHART_HEIGHT = 320
const CHART_MARGIN = {top: 24, right: 8, left: 16, bottom: 60}

const SIDE_BY_SIDE_MIN_WIDTH = '100rem'

type Props = {
    data: ProspectsByGivingLevelData
    onProspectFilter?: (filters: ProspectClickFilters) => void
}

type ChartTableLayout = 'stack' | 'sideBySide'
type ChartTableContent = 'chart' | 'table' | 'both'

type EstimateRangeChartRow = {
    capacityBucket: CapacityBucket
    low: number
    range: number
    high: number
}

function filtersForCapacityBucket(bucket: CapacityBucket): ProspectClickFilters {
    return {capacityBucket: bucket}
}

/** Prominent low/high portfolio totals and range bar above the chart/table. */
export function EstimateRangeSummary({data}: {data: ProspectsByGivingLevelData}) {
    const totalProspects = data.rows.reduce((s, r) => s + r.prospects, 0)

    return (
        <Stack gap="md" data-testid="estimate-range-summary">
            <Box
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
                    columnGap: 'var(--mantine-spacing-xs)',
                    rowGap: 4,
                    alignItems: 'end',
                }}
            >
                <Text
                    size="xs"
                    c="dimmed"
                    tt="uppercase"
                    fw={600}
                    style={{gridColumn: 1, gridRow: 1}}
                >
                    Low Total Estimate
                </Text>
                <Text
                    size="xs"
                    c="dimmed"
                    tt="uppercase"
                    fw={600}
                    ta="right"
                    style={{gridColumn: 3, gridRow: 1}}
                >
                    High Total Estimate
                </Text>
                <Text fz={32} fw={700} lh={1.2} style={{gridColumn: 1, gridRow: 2, minWidth: 0}}>
                    {formatCurrency(data.totalLowEstimate)}
                </Text>
                <Text
                    size="lg"
                    c="dimmed"
                    ta="center"
                    style={{
                        gridColumn: 2,
                        gridRow: 2,
                        whiteSpace: 'nowrap',
                        paddingInline: 4,
                        lineHeight: 1,
                    }}
                >
                    {formatNumber(totalProspects)} Prospects
                </Text>
                <Text
                    fz={32}
                    fw={700}
                    lh={1.2}
                    ta="right"
                    style={{gridColumn: 3, gridRow: 2, minWidth: 0}}
                >
                    {formatCurrency(data.totalHighEstimate)}
                </Text>
            </Box>
            <Group gap={6} align="center" wrap="nowrap" aria-hidden>
                <Box
                    w={6}
                    h={6}
                    bg={CHART_COLORS.secondary}
                    style={{borderRadius: '50%', flexShrink: 0}}
                />
                <Box
                    style={{
                        flex: 1,
                        height: 6,
                        borderRadius: 9999,
                        background: 'var(--mantine-color-gray-2)',
                        overflow: 'hidden',
                    }}
                >
                    <Box h="100%" w="100%" bg={CHART_COLORS.secondary} />
                </Box>
                <Box
                    w={6}
                    h={6}
                    bg={CHART_COLORS.secondary}
                    style={{borderRadius: '50%', flexShrink: 0}}
                />
            </Group>
        </Stack>
    )
}

function ProspectsByGivingLevelChartAndTable({
    data,
    onProspectFilter,
    layout = 'stack',
    content = 'both',
}: {
    data: ProspectsByGivingLevelData
    onProspectFilter?: (filters: ProspectClickFilters) => void
    layout?: ChartTableLayout
    content?: ChartTableContent
}) {
    const sortedRows = [...data.rows]
    const chartRows: EstimateRangeChartRow[] = sortedRows.map((row) => {
        const low = Number(row.lowTotalEstimate)
        const high = Number(row.highTotalEstimate)
        return {
            capacityBucket: row.capacityBucket,
            low,
            range: Math.max(0, high - low),
            high,
        }
    })
    const chartColumnRef = useRef<HTMLDivElement>(null)
    const [sideBySideChartHeight, setSideBySideChartHeight] = useState(CHART_HEIGHT)

    useLayoutEffect(() => {
        if (layout !== 'sideBySide' || content !== 'both') {
            return
        }
        const el = chartColumnRef.current
        if (!el) {
            return
        }
        const updateHeight = () => {
            const h = el.getBoundingClientRect().height
            if (h > 0) {
                setSideBySideChartHeight(Math.floor(h))
            }
        }
        updateHeight()
        const observer = new ResizeObserver(updateHeight)
        observer.observe(el)
        return () => observer.disconnect()
    }, [layout, content, sortedRows.length, data.totalLowEstimate, data.totalHighEstimate])

    const chartPixelHeight =
        layout === 'sideBySide' && content === 'both' ? sideBySideChartHeight : CHART_HEIGHT
    const plotHeight = chartPixelHeight - CHART_MARGIN.top - CHART_MARGIN.bottom

    const renderBarShape = useCallback(
        (props: unknown) => {
            const p = props as {
                x?: number
                y?: number
                width?: number
                height?: number
                fill?: string
                radius?: [number, number, number, number]
                payload?: {capacityBucket?: CapacityBucket}
            }
            const {x = 0, y = 0, width = 0, height = 0, fill, radius, payload} = p
            const filters = payload?.capacityBucket
                ? filtersForCapacityBucket(payload.capacityBucket)
                : null

            return (
                <g>
                    <Rectangle
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={fill ?? CHART_COLORS.secondary}
                        radius={radius ?? [4, 4, 4, 4]}
                        style={onProspectFilter && filters ? {cursor: 'pointer'} : undefined}
                    />
                    {onProspectFilter && filters && (
                        <rect
                            x={x}
                            y={0}
                            width={width}
                            height={plotHeight}
                            fill="transparent"
                            style={{cursor: 'pointer'}}
                            onClick={() => onProspectFilter(filters)}
                        />
                    )}
                </g>
            )
        },
        [onProspectFilter, plotHeight],
    )

    const chart = (
        <ResponsiveContainer width="100%" height={chartPixelHeight}>
            <BarChart data={chartRows} margin={CHART_MARGIN} style={{overflow: 'visible'}}>
                <CartesianGrid {...CARTESIAN_GRID_PROPS} />
                <XAxis
                    dataKey="capacityBucket"
                    angle={-35}
                    textAnchor="end"
                    height={80}
                    interval={0}
                />
                <YAxis
                    type="number"
                    allowDecimals={false}
                    tickFormatter={(v: number) => formatCurrency(v)}
                    width={88}
                />
                <Tooltip
                    content={({active, payload, label}) => {
                        if (!active || !payload?.length) {
                            return null
                        }
                        const first = payload[0]
                        if (!first?.payload) {
                            return null
                        }
                        const row = first.payload as EstimateRangeChartRow
                        return (
                            <Paper p="xs" shadow="sm" withBorder>
                                <Stack gap={4}>
                                    <Text size="sm" fw={600}>
                                        {label}
                                    </Text>
                                    <Text size="sm">Low: {formatCurrency(row.low)}</Text>
                                    <Text size="sm">High: {formatCurrency(row.high)}</Text>
                                </Stack>
                            </Paper>
                        )
                    }}
                />
                <Bar
                    dataKey="low"
                    stackId="estimate"
                    fill="transparent"
                    isAnimationActive={false}
                />
                <Bar
                    dataKey="range"
                    stackId="estimate"
                    fill={CHART_COLORS.secondary}
                    radius={[4, 4, 4, 4]}
                    isAnimationActive={false}
                    shape={renderBarShape}
                >
                    <LabelList
                        dataKey="high"
                        position="top"
                        formatter={(v: number) => formatCurrency(v)}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    )

    const table = (
        <Box
            maw={layout === 'stack' ? 660 : undefined}
            w="100%"
            mt={layout === 'stack' ? undefined : 0}
        >
            <Table striped highlightOnHover layout="auto">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Capacity</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Prospects</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>Low Total Estimate</Table.Th>
                        <Table.Th style={{textAlign: 'right'}}>High Total Estimate</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {sortedRows.map((row) => {
                        const handleClick = onProspectFilter
                            ? () => onProspectFilter(filtersForCapacityBucket(row.capacityBucket))
                            : undefined
                        return (
                            <Table.Tr key={row.capacityBucket}>
                                <Table.Td
                                    onClick={handleClick}
                                    style={handleClick ? {cursor: 'pointer'} : undefined}
                                >
                                    {row.capacityBucket}
                                </Table.Td>
                                <Table.Td
                                    onClick={handleClick}
                                    style={
                                        handleClick
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatNumber(row.prospects)}
                                </Table.Td>
                                <Table.Td
                                    onClick={handleClick}
                                    style={
                                        handleClick
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatCurrency(row.lowTotalEstimate)}
                                </Table.Td>
                                <Table.Td
                                    onClick={handleClick}
                                    style={
                                        handleClick
                                            ? {textAlign: 'right', cursor: 'pointer'}
                                            : {textAlign: 'right'}
                                    }
                                >
                                    {formatCurrency(row.highTotalEstimate)}
                                </Table.Td>
                            </Table.Tr>
                        )
                    })}
                </Table.Tbody>
                <Table.Tfoot>
                    <Table.Tr>
                        <Table.Td>
                            <strong>Total</strong>
                        </Table.Td>
                        <Table.Td style={{textAlign: 'right'}}>
                            <strong>
                                {formatNumber(sortedRows.reduce((s, r) => s + r.prospects, 0))}
                            </strong>
                        </Table.Td>
                        <Table.Td style={{textAlign: 'right'}}>
                            <strong>{formatCurrency(data.totalLowEstimate)}</strong>
                        </Table.Td>
                        <Table.Td style={{textAlign: 'right'}}>
                            <strong>{formatCurrency(data.totalHighEstimate)}</strong>
                        </Table.Td>
                    </Table.Tr>
                </Table.Tfoot>
            </Table>
        </Box>
    )

    if (content === 'chart') {
        return chart
    }
    if (content === 'table') {
        return table
    }

    if (layout === 'sideBySide') {
        return (
            <Group align="stretch" gap="lg" wrap="nowrap">
                <Box
                    ref={chartColumnRef}
                    style={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box style={{flex: 1, minHeight: 0, width: '100%'}}>{chart}</Box>
                </Box>
                <Box style={{flex: 1, minWidth: 0, maxWidth: 660, overflow: 'auto'}}>{table}</Box>
            </Group>
        )
    }

    return (
        <Stack gap="lg">
            {chart}
            {table}
        </Stack>
    )
}

export function ProspectsByGivingLevelExportContent({
    data,
    kind,
}: {
    data: ProspectsByGivingLevelData
    kind: ExportKind
}) {
    if (kind === 'chart') {
        return (
            <Stack gap="lg">
                <EstimateRangeSummary data={data} />
                <ProspectsByGivingLevelChartAndTable data={data} layout="stack" content="chart" />
            </Stack>
        )
    }
    return <ProspectsByGivingLevelChartAndTable data={data} layout="stack" content="table" />
}

export default function ProspectsByGivingLevel({data, onProspectFilter}: Props) {
    const isWideLayout = useMediaQuery(`(min-width: ${SIDE_BY_SIDE_MIN_WIDTH})`, false, {
        getInitialValueInEffect: false,
    })

    return (
        <SectionCard title="Prospects by Giving Level">
            <Stack gap="lg">
                <EstimateRangeSummary data={data} />
                <ProspectsByGivingLevelChartAndTable
                    data={data}
                    onProspectFilter={onProspectFilter}
                    layout={isWideLayout ? 'sideBySide' : 'stack'}
                />
            </Stack>
        </SectionCard>
    )
}
